import React, { FC, memo, useMemo } from "react";
import { useSxMerge, useTimeBandXAxis } from "shared-ts-mui";
import { LinearProgress, Stack, SxProps, useTheme } from "@mui/material";
import {
  BarPlot,
  ChartDataProviderPro,
  ChartsAxisHighlight,
  ChartsAxisTooltipContent,
  ChartsLegend,
  ChartsReferenceLine,
  ChartsSurface,
  ChartsTooltipContainer,
  ChartsXAxis,
  ChartsYAxis,
  LinePlot,
} from "@mui/x-charts-premium";
import { useSeries } from "./useSeries";
import { useTranslation } from "react-i18next";
import { useYAxis } from "./useYAxis";
import { NeedForSweepDataPoints } from "./NeedForSweepDataPoints";
import { UseQueryResult } from "@tanstack/react-query";
import { Sweeping } from "../../sweeping/SweepingDto";
import { useDataset } from "./useDataset";
import { DateTime } from "luxon";
import { DateTimeUtils, useDateTimeNow } from "shared-ts-utils-luxon";
import { DateTimeISO } from "shared-ts-types";
import { IllegalStateError } from "shared-ts-utils-core";

export interface NeedForSweepChartProps {
  loading?: boolean;
  flue: {
    audit: {
      created: DateTime<true> | DateTimeISO;
    };
    sensorOperationalAt?: DateTime<true> | DateTimeISO;
  };
  needForSweep: NeedForSweepDataPoints;
  startDate?: DateTime<true>;
  sweepings: UseQueryResult<Sweeping[], Error>;
  hideBurnHours?: boolean;
  hidePerPeriod?: boolean;
  hideLegend?: boolean;
  hideAxisLabels?: boolean;
  hideNowReferenceLine?: boolean;
  hideReferenceLines?: boolean;
  displaySootIndex?: boolean;
  sootIndexThreshold: number;
  burnHoursThreshold: number;
  sx?: SxProps;
  onUserInteraction?: () => void;
}

export const NeedForSweepChart: FC<NeedForSweepChartProps> = memo((props) => {
  const flueCreated = useMemo(
    () =>
      typeof props.flue.audit.created === "string"
        ? DateTimeUtils.fromISO(props.flue.audit.created)
        : props.flue.audit.created,
    [props.flue.audit.created]
  );

  if (
    props.startDate !== undefined &&
    props.startDate < flueCreated.startOf("month")
  ) {
    throw new IllegalStateError(
      `NeedForSweepChart.startDate (${props.startDate.toISO()}) cannot be before flue creation: ${flueCreated.toISO()}`
    );
  }
  const theme = useTheme();
  const { t } = useTranslation("firefly-shared-ts-domain");
  const hideBurnHours = props.hideBurnHours ?? false;
  const hidePerPeriod = props.hidePerPeriod ?? false;
  const hideLegend = props.hideLegend ?? false;
  const hideAxisLabels = props.hideAxisLabels ?? false;
  const hideNowReferenceLine = props.hideNowReferenceLine ?? false;
  const hideReferenceLines = props.hideReferenceLines ?? false;
  const displaySootIndex = props.displaySootIndex ?? false;
  const loading = props.loading ?? false;
  const now = useDateTimeNow();
  const nowStartOfMonth = useMemo(
    () => now.startOf("month").plus({ month: 1 }),
    [now]
  );
  const svgRef = React.useRef<SVGSVGElement>(null);
  const dataset = useDataset(props.needForSweep, props.startDate);
  const series = useSeries(hideBurnHours, hidePerPeriod, displaySootIndex);
  const startDate = useMemo(() => {
    if (props.startDate != null) {
      return props.startDate;
    }
    if (props.needForSweep.length === 0) {
      return nowStartOfMonth;
    }
    return props.needForSweep.firstOrThrow().time.startOf("month");
  }, [props.needForSweep, props.startDate, nowStartOfMonth]);
  const endDate = useMemo(() => {
    if (props.needForSweep.length === 0) {
      return nowStartOfMonth.plus({ months: 1 });
    }
    return props.needForSweep
      .lastOrThrow()
      .time.startOf("month")
      .plus({ months: 1 });
  }, [props.needForSweep, nowStartOfMonth]);

  const period = endDate.diff(startDate);
  const periodYears = period.as("years");

  const xAxis = useTimeBandXAxis("month", startDate, endDate, {
    tickInterval: (value, index) => periodYears < 3 || index % 3 === 0, // show every 3d tick
    grouping: {
      enabled: true,
      monthFormat: "MM",
    },
  });
  const yAxis = useYAxis(
    props.needForSweep,
    props.sootIndexThreshold,
    props.burnHoursThreshold,
    hideAxisLabels
  );

  const sensorOperationalAt = useMemo(
    () =>
      typeof props.flue.sensorOperationalAt === "string"
        ? DateTimeUtils.fromISO(props.flue.sensorOperationalAt).startOf("month")
        : props.flue.sensorOperationalAt,
    [props.flue.sensorOperationalAt]
  );

  const sx = useSxMerge(props.sx, {
    position: "relative",
  });

  return (
    <Stack className={"NeedForSweepChart"} direction={"column"} sx={sx}>
      {loading && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
          }}
        />
      )}

      <ChartDataProviderPro
        margin={{
          top: 20,
          left: !hideAxisLabels ? 10 : 0,
          right: !hideAxisLabels ? 85 : 50,
          bottom: 20,
        }}
        xAxis={xAxis}
        yAxis={yAxis}
        dataset={dataset}
        series={series}
        onAxisClick={(e) => {
          if (props.onUserInteraction) props.onUserInteraction();
        }}
      >
        <ChartsSurface
          ref={svgRef}
          sx={{
            ".MuiMarkElement-root": {
              scale: "1.2",
            },
            ".MuiLineElement-root": {
              strokeWidth: 5,
              filter: "drop-shadow(2px 3px 3px rgba(0, 0, 0, 0.9))",
            },
            ".MuiLineElement-series-predictedSootingDegreeCumulative": {
              strokeDasharray: "5 5",
            },
            ".MuiLineElement-series-predictedSootIndexCumulative": {
              strokeDasharray: "5 5",
            },
            ".MuiLineElement-series-predictedBurnHoursCumulative": {
              strokeDasharray: "5 5",
            },
          }}
        >
          {!hideReferenceLines && !hideNowReferenceLine && (
            <ChartsReferenceLine
              axisId={"timeBand"}
              x={nowStartOfMonth as unknown as Date}
              label={t("Now")}
              labelAlign={"middle"}
              labelStyle={{
                fontSize: "smaller",
                angle: -25,
              }}
              lineStyle={{
                strokeDasharray: "10 5",
                stroke: theme.palette.grey["500"],
              }}
            />
          )}
          {!hideReferenceLines &&
            sensorOperationalAt !== undefined &&
            sensorOperationalAt < flueCreated && (
              <ChartsReferenceLine
                axisId={"timeBand"}
                x={sensorOperationalAt as unknown as Date}
                label={t("Sensor Operational")}
                labelStyle={{
                  fontSize: "smaller",
                  angle: -25,
                }}
                lineStyle={{
                  strokeDasharray: "10 5",
                  stroke: theme.palette.grey["500"],
                }}
              />
            )}
          {!hideReferenceLines &&
            props.sweepings.data?.map((sweeping) => {
              return (
                <ChartsReferenceLine
                  key={sweeping.id}
                  axisId={"timeBand"}
                  x={sweeping.time.startOf("month") as unknown as Date}
                  label={t("Sweep")}
                  labelStyle={{
                    fontSize: "smaller",
                    angle: -25,
                  }}
                  lineStyle={{
                    strokeDasharray: "10 5",
                    stroke: theme.palette.grey["500"],
                  }}
                />
              );
            })}
          {!hideReferenceLines && (
            <ChartsReferenceLine
              y={displaySootIndex ? props.sootIndexThreshold : 100}
              label={
                displaySootIndex
                  ? t("Need for Sweep at Soot Index {{value}}", {
                      value: props.sootIndexThreshold,
                    })
                  : t("Need for Sweep at Sooting Degree {{value}}", {
                      value: "100 %",
                    })
              }
              labelAlign={"end"}
              labelStyle={{
                fontSize: "smaller",
              }}
              lineStyle={{
                strokeDasharray: "10 5",
                stroke: theme.palette.grey["500"],
              }}
              axisId={displaySootIndex ? "sootIndex" : "sootingDegree"}
            />
          )}
          {!hideReferenceLines && !hideBurnHours && (
            <ChartsReferenceLine
              y={props.burnHoursThreshold}
              label={t("Need for Sweep at {{value}} Burn Hours", {
                value: props.burnHoursThreshold,
              })}
              labelAlign={"start"}
              labelStyle={{
                fontSize: "smaller",
              }}
              lineStyle={{
                strokeDasharray: "10 5",
                stroke: theme.palette.grey["500"],
              }}
              axisId={"burnHours"}
            />
          )}
          <ChartsXAxis position="bottom" axisId="timeBand" />
          {!hideBurnHours && (
            <ChartsYAxis
              label={!hideAxisLabels ? t("Burn Hours") : undefined}
              position="left"
              axisId="burnHours"
            />
          )}
          {!displaySootIndex && (
            <ChartsYAxis
              label={!hideAxisLabels ? t("Sooting Degree") : undefined}
              position="right"
              axisId="sootingDegree"
            />
          )}
          {displaySootIndex && (
            <ChartsYAxis
              label={!hideAxisLabels ? t("Soot Index") : undefined}
              position="right"
              axisId="sootIndex"
            />
          )}
          <BarPlot />
          <LinePlot />
          <ChartsAxisHighlight x="band" />
          <ChartsTooltipContainer trigger={"axis"}>
            <ChartsAxisTooltipContent />
          </ChartsTooltipContainer>
        </ChartsSurface>
        {!hideLegend && (
          <ChartsLegend
            direction={"horizontal"}
            sx={{ justifyContent: "center" }}
          />
        )}
      </ChartDataProviderPro>
    </Stack>
  );
});
